/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Checkbox,
  Chip,
  Popper,
  PopperProps,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles/';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import clsx from 'clsx';
import appTheme from '../../common/theme/appTheme.json';
import CustomAutocompletePaper from './CustomAutocompletePaper';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { isNil } from 'lodash';

const ALL_ITEMS_TEXT = 'Select all';

enum CheckBoxState {
  NONE,
  INDETERMINATE,
  ALL,
}
export interface SelectBoxProps<T> {
  options: T[];
  getOptionLabel: (item: T) => string;
  children?: string;
  isDisabled?: boolean;
  filter?: (options: any[], state: FilterOptionsState<any>) => any[];
  placeholderText: string;
  multipleSelectedText: string;
  onSelectboxValueChanged: (values: T[]) => void;
  isOptionEqualToValue: (val: T, option: T) => boolean;
  popperWidth?: number | string;
  defaultValue?: T | undefined | null;
  selectAllByDefault?: boolean | undefined | null;
}

export const useStyles = makeStyles({
  select: {
    backgroundColor: appTheme.primaryBackgroundColor,
    fontSize: '16px',
    fontFamily: appTheme.appFontFamily,
    borderRadius: '5px',
    paddingLeft: '10px',
    WebkitBoxSizing: 'border-box',
    MozBoxSizing: 'border-box',
    boxSizing: 'border-box',
    width: '100%',
    height: '38px',
  },
  selectText: {
    paddingLeft: '10px',
  },
  enabled: {
    color: appTheme.primaryTextColor,
    border: `1px solid #9fafbc`,
    backgroundColor: appTheme.mptPrimaryBackgroundColor,
    outline: '0',
    '&.Mui-focused': {
      border: `1px solid ${appTheme.mptLightBlue}`,
      borderBottomRightRadius: '0px',
      borderBottomLeftRadius: '0px',
    },
  },
  disabled: {
    color: appTheme.mptGray,
    border: `1px solid ${appTheme.mptGray}`,
  },
  placeholderEnabled: {
    '&::placeholder': {
      color: appTheme.primaryTextColor,
      opacity: 1,
    },
    '&:focus::placeholder': {
      opacity: 0.5,
    },
  },
  placeholderDisabled: {
    '&::placeholder': {
      color: appTheme.mptGray,
      opacity: 0.5,
    },
  },
  autocompleteRoot: {
    '& .MuiAutocomplete-popupIndicator': {
      color: appTheme.mptSelectIconGray,
    },
    '& .MuiAutocomplete-clearIndicator': {
      display: 'none',
    },
    '& fieldset': {
      border: 'none !important',
    },
  },
  autocompleteNoOptions: { color: appTheme.primaryTextColor },
  autocompleteOptions: {
    backgroundColor: appTheme.primaryBackgroundColor,
    '&:hover': {
      backgroundColor: appTheme.secondaryBackgroundColor,
    },
  },
  fieldset: {
    border: 0,
  },
});

function DropdownListItem<T>({
  selected,
  option,
  selectAllState,
  getOptionLabel,
  ...props
}: React.HTMLAttributes<HTMLLIElement> & {
  selected: boolean;
  option: T | typeof ALL_ITEMS_TEXT;
  selectAllState: CheckBoxState;
  getOptionLabel: (item: T | typeof ALL_ITEMS_TEXT) => string;
}) {
  return (
    <li {...props}>
      <Checkbox
        checked={selected}
        indeterminate={option === ALL_ITEMS_TEXT && selectAllState === CheckBoxState.INDETERMINATE}
        sx={{
          color: appTheme.primaryTextColor,
          '&.Mui-checked': {
            color: appTheme.primaryTextColor,
          },
          '&.MuiCheckbox-indeterminate': {
            color: appTheme.primaryTextColor,
          },
        }}
      />
      <span>{getOptionLabel(option)}</span>
    </li>
  );
}

function AutocompleteSelect<T>(props: React.PropsWithChildren<SelectBoxProps<T>>) {
  const {
    onSelectboxValueChanged,
    options,
    getOptionLabel,
    isDisabled,
    filter,
    placeholderText,
    multipleSelectedText,
    isOptionEqualToValue,
    popperWidth,
    defaultValue,
    selectAllByDefault
  } = props;
  const classes = useStyles();
  const [selectOptions, setSelectOptions] = useState<(T | typeof ALL_ITEMS_TEXT)[]>([]);
  const calculateInitialBoxValue = () => {
    if (selectAllByDefault) {
      // If selectAllByDefault is set to true, select all the values in the Autocomplete Selector
      const allValuesArray = [ALL_ITEMS_TEXT, ...options] as T[];
      return allValuesArray
    } else if (!isNil(defaultValue)) {
      return [defaultValue]
    } else {
      const emptyValueArray: T[] = [];
      return emptyValueArray;
    }
  }
  const [selectboxValue, setSelectboxValue] = useState<(T | typeof ALL_ITEMS_TEXT)[]>(calculateInitialBoxValue());
  const [selectAllValue, setSelectAllValue] = useState<CheckBoxState>(selectAllByDefault === true ? CheckBoxState.ALL : CheckBoxState.NONE);

  useEffect(() => {
    if (options && options.length) {
      setSelectOptions([ALL_ITEMS_TEXT, ...options]);
    }
  }, [options]);

  useEffect(() => {
    onSelectboxValueChanged(selectboxValue.filter(val => val !== ALL_ITEMS_TEXT) as T[]);

    // Should not pass onSelectboxValueChanged in the dependency array because this component can not enforce
    // that the callback is memoized. If the callback is not memoized it would result in an endless loop.
    // If callback is not memoized:
    // selectboxValue changed => parent component re-renders => sends new onSelectboxValueChanged => this effect runs and selectboxValue changed => ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectboxValue]);

  /**
   * Handles the change of the autocomplete items.
   * When the select all button is clicked it will follow the given state machine flow:
   *    NONE -> INDETERMINATE -> ALL -> NONE
   * @param event the react event
   * @param value all values currently selected based on the users last interaction with one of the items
   */
  const handleChange = (event: React.ChangeEvent<{}>, value: (T | typeof ALL_ITEMS_TEXT)[]) => {
    event.preventDefault();

    if (value && value.length && !value.find(val => val === ALL_ITEMS_TEXT) && selectAllValue === CheckBoxState.ALL) {
      // select all was selected when all items were selected
      setSelectboxValue([]);
      setSelectAllValue(CheckBoxState.NONE);
    } else if (value && value.length && value[value.length - 1] === ALL_ITEMS_TEXT) {
      // the most recent change was selecting the select all option
      setSelectboxValue([ALL_ITEMS_TEXT, ...options]);
      setSelectAllValue(CheckBoxState.ALL);
    } else {
      //Sets the value of the select box
      setSelectboxValue(value);
      if (value && value.length) {
        setSelectAllValue(CheckBoxState.INDETERMINATE);
      } else {
        setSelectAllValue(CheckBoxState.NONE);
      }
    }
  };

  //Prevent dropdowns from submitting with 'Enter' key
  const onKeyDown = (keyEvent: { key: string; preventDefault: () => void }) => {
    if (keyEvent.key === 'Enter') {
      keyEvent.preventDefault();
    }
  };

  const getOptionLabelWrapper = (item: T | typeof ALL_ITEMS_TEXT) => {
    if (item === ALL_ITEMS_TEXT) {
      return ALL_ITEMS_TEXT;
    }

    return getOptionLabel(item);
  };

  const isOptionEqualToValueWrapper = (option: T | typeof ALL_ITEMS_TEXT, val: T | typeof ALL_ITEMS_TEXT) => {
    if (option === ALL_ITEMS_TEXT && val === ALL_ITEMS_TEXT) {
      return true;
    } else if (option === ALL_ITEMS_TEXT || val === ALL_ITEMS_TEXT) {
      return false;
    } else {
      return isOptionEqualToValue(option, val);
    }
  };

  return (
    <Autocomplete<T | typeof ALL_ITEMS_TEXT, true, true, false>
      disablePortal
      multiple
      limitTags={0}
      options={selectOptions}
      onKeyDown={onKeyDown}
      getOptionLabel={getOptionLabelWrapper}
      filterOptions={filter ? (option, state) => filter(option, state) : undefined}
      isOptionEqualToValue={isOptionEqualToValueWrapper}
      classes={{
        root: classes.autocompleteRoot,
        noOptions: classes.autocompleteNoOptions,
        option: classes.autocompleteOptions,
      }}
      value={selectboxValue}
      PaperComponent={CustomAutocompletePaper as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
      PopperComponent={CustomPopper}
      componentsProps={{ popper: { width: popperWidth } as any }}
      popupIcon={<KeyboardArrowDown />}
      disabled={isDisabled}
      autoComplete={false}
      disableCloseOnSelect
      disableClearable
      fullWidth
      onChange={(event, value) => handleChange(event, value)}
      renderTags={(value: (T | typeof ALL_ITEMS_TEXT)[], getTagProps: AutocompleteRenderGetTagProps) => {
        return (
          <Chip
            sx={{
              backgroundColor: appTheme.mptLightBlue,
              color: appTheme.darkGrayTextColor,
              fontFamily: appTheme.appFontFamily,
              lineHeight: '24px',
              fontSize: '18px',
              position: 'absolute',
              right: '40px',
            }}
            label={value.filter(val => val !== ALL_ITEMS_TEXT).length}
            onDelete={() => {
              setSelectboxValue([]);
              setSelectAllValue(CheckBoxState.NONE);
            }}
          />
        );
      }}
      renderOption={(
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T | typeof ALL_ITEMS_TEXT,
        state: AutocompleteRenderOptionState,
      ) => (
        <DropdownListItem
          option={option}
          selected={state.selected}
          selectAllState={selectAllValue}
          getOptionLabel={getOptionLabelWrapper}
          {...props}
        />
      )}
      renderInput={(params: AutocompleteRenderInputParams) => {
        let placeholder = placeholderText;

        if (selectboxValue.length > 1) {
          placeholder = multipleSelectedText;
        } else if (selectboxValue.length && selectboxValue[0] !== ALL_ITEMS_TEXT) {
          placeholder = getOptionLabel(selectboxValue[0]);
        }

        return (
          <TextField
            {...params}
            placeholder={placeholder}
            fullWidth
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              className: clsx(classes.select, {
                [classes.enabled]: !isDisabled,
                [classes.disabled]: isDisabled,
              }),
            }}
            inputProps={{
              ...params.inputProps,
              className: clsx(classes.selectText, {
                [classes.placeholderEnabled]: !isDisabled,
                [classes.placeholderDisabled]: isDisabled,
              }),
            }}
            sx={{
              fieldset: {
                border: 0,
              },
            }}
          />
        );
      }}
    />
  );
}

export default AutocompleteSelect;

// Custom Popper in order to get the correct width for the styling
function CustomPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      style={{
        //@ts-ignore
        width: props.width || '100%',
      }}
    />
  );
}
